import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { findRouteMeta, getDefaultOgImage } from "../config/routeMeta";
import { useDocumentHead } from "../contexts/DocumentHeadContext";

const TITLE_PREFIX = "Agnes RH";

function setMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (el) {
    el.setAttribute("content", content);
  } else {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.setAttribute("content", content);
    document.head.appendChild(el);
  }
}

export default function RouteDocumentHead() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const { meta: overrideMeta } = useDocumentHead();

  useEffect(() => {
    const routeMeta = findRouteMeta(pathname);
    const titleFromRoute = routeMeta
      ? (routeMeta.title ?? (routeMeta.titleKey ? t(routeMeta.titleKey) : ""))
      : "";
    const defaultTitle = titleFromRoute
      ? `${TITLE_PREFIX} - ${titleFromRoute}`
      : TITLE_PREFIX;
    const defaultDescription = routeMeta?.descriptionKey
      ? t(routeMeta.descriptionKey)
      : "";
    const defaultOgImage = routeMeta?.ogImage || getDefaultOgImage();
    const url = typeof window !== "undefined" ? window.location.href : "";

    const title =
      overrideMeta?.title != null && overrideMeta.title !== ""
        ? overrideMeta.title
        : defaultTitle;
    const description =
      overrideMeta?.ogDescription ??
      overrideMeta?.description ??
      (defaultDescription || title);
    const ogTitle = overrideMeta?.ogTitle ?? title;
    const ogDescription = overrideMeta?.ogDescription ?? overrideMeta?.description ?? description;
    const ogImage =
      overrideMeta?.ogImage != null && overrideMeta.ogImage !== ""
        ? overrideMeta.ogImage
        : defaultOgImage;
    const ogType = overrideMeta?.ogType ?? "website";

    document.title = title;

    setMeta("description", description);
    setMeta("og:title", ogTitle, true);
    setMeta("og:description", ogDescription, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", url, true);
    if (ogImage) setMeta("og:image", ogImage, true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", ogTitle);
    setMeta("twitter:description", ogDescription);
    if (ogImage) setMeta("twitter:image", ogImage);
  }, [pathname, t, i18n.language, overrideMeta]);

  return null;
}
